import { MinzeElement, Attrs, Reactive, EventListeners } from 'minze'
import MainModule from '../../modules/main'
import { Locale } from '../../modules/locale'
import { AdditionalTypes } from '../../modules/additionalTypes'
import { Callback } from '../../modules/callback'

Locale.getInstance().registerKeySet('fr', 'lvcAccountCulturalProfile', {
  title: 'Mon profil culturel',
  description:
    'Indiquez vos intérêts culturels pour personnaliser votre expérience sur La Vitrine!',
  changeSavedMsg: 'Vos préférences ont été enregistrées avec succès!',
})

Locale.getInstance().registerKeySet('en', 'lvcAccountCulturalProfile', {
  title: 'My cultural profile',
  description: 'Share your cultural interests to personalize your experience on La Vitrine!',
  changeSavedMsg: 'Your preferences have been saved successfully!',
})

export interface LvcAccountCulturalProfile {
  titleSeparator: boolean
  showToasts: boolean
  userSelection: { [key: string]: string[] }
  selectedCategories: string[]
}

export class LvcAccountCulturalProfile extends MinzeElement {
  attrs: Attrs = [
    ['title-separator', false],
    ['show-toasts', true],
  ]
  static observedAttributes = ['titleSeparator']
  private toggleCb: string = Callback.attach(this.onToggleMainCategory.bind(this))
  reactive: Reactive = [['userSelection', {}]]
  categories: any[] = []

  updateUserSelection = async (): Promise<void> => {
    const mainModule = MainModule.getInstance()
    const userData = await mainModule.getAuth().getUserData()

    if (userData?.profile?.culturalProfile) {
      this.userSelection = userData.profile?.culturalProfile
    } else {
      this.userSelection = {}
    }

    this.rerender()
  }

  async onReady(): Promise<void> {
    this.categories = await AdditionalTypes.getInstance().getCategories()
    this.rerender()
    await this.updateUserSelection()
  }

  async onToggleMainCategory(toggledState: boolean, categoryId: string): Promise<void> {
    this.saveCategory(toggledState, categoryId)
  }

  saveCategory = async (toggledState: boolean, categoryId: string) => {
    const mainModule = MainModule.getInstance()
    try {
      // GTM Event Tracking 'mon_profil_toggle_discipline'
      await mainModule.analyticsTrack('mon_profil_toggle_discipline', {
        link_text: this.categories.find(cat => cat.code === categoryId)?.label || categoryId,
        type: toggledState ? 'activer' : 'désactiver',
      })
    } catch (error) {
      console.error('Error tracking GTM event:', error)
    }
    // If the toggled state is false, remove the categoryId from the userSelection
    if (!toggledState) {
      // Remove entire category from userSelection
      this.userSelection[categoryId] = []
    } else {
      // if the toggled state is true
      // Add the categoryId to the userSelection if it's not already there
      if (!this.userSelection[categoryId]) {
        this.userSelection[categoryId] = [categoryId]
      } else if (!this.userSelection[categoryId].includes(categoryId)) {
        this.userSelection[categoryId].push(categoryId)
      }
    }

    // Save the userSelection with a delay to prevent spamming
    clearTimeout(this.saveTimeout as NodeJS.Timeout)
    this.saveTimeout = setTimeout(async () => {
      await mainModule.getAuth().saveProfile(`profile-culture`, this.userSelection)
      if (this.showToasts)
        mainModule.showToastAlert(Locale.getKey('lvcAccountCulturalProfile.changeSavedMsg'))
    }, 1000)
    this.rerender()
  }

  private renderCategory(category: any) {
    let { code, label } = category
    // check if user has selected any subcategories for this category
    let userSelectionHasCategory = this?.userSelection?.[category.code]?.length > 0 || false

    return /* html */ `
      <lvc-hand-drawn-box variant="button2">
        <div class="account-cultural-profile-category">
          <div class="account-cultural-profile-category-label">${label}</div>
          <div class="account-cultural-profile-category-toggle">
            <lvc-toggle id=${code} class="toggle" on-click="${this.toggleCb}" initial-toggled="${userSelectionHasCategory}"></lvc-toggle>
          </div>
        </div>
      </lvc-hand-drawn-box>
    `
  }

  html = () => `
    <div class="account-cultural-profile">
      <lvc-account-header
        label="${Locale.getKey('lvcAccountCulturalProfile.title')}"
        description="${Locale.getKey('lvcAccountCulturalProfile.description')}"
        separator="${this.titleSeparator}"
      >
        <lvc-account-theater-icon width="44" height="44" slot="icon"></lvc-account-theater-icon>
      </lvc-account-header>
      <div class="account-cultural-profile-categories">
        ${this.categories.map(category => this.renderCategory(category)).join('')}
      </div>
    </div>
  `

  eventListeners: EventListeners = [
    [window, 'lvc:user_state_changed', this.updateUserSelection.bind(this)],
  ]

  css = () => `
    :host {}

    .account-cultural-profile-categories {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .account-cultural-profile-category {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 10px 10px;
    }

    .account-cultural-profile-category-label {
      color: #1E007D;
      font-size: 1.125em;
      font-weight: 800;
      line-height: 120%;
      text-transform: uppercase;
    }

    .account-cultural-profile-category-toggle {}

    @media only screen and (min-width: 992px) {
      .account-cultural-profile-categories {
        gap: 15 px;
      }

      .account-cultural-profile-category {
        padding: 10px 20px;
      }

      .account-cultural-profile-category-label {
        font-size: 1.375em;
      }

      .account-cultural-profile-category-toggle {}
    }
  `
}
